import React from 'react';
import {
  Box,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

// Day log for multi-day rentals (2026-09-27). A multi-day rental is billed day
// by day, so the trip's times, distances and odometer readings live here, one
// block per day, and every day is correctable: its window, its odometer
// readings, and which distance bills it. The GPS figure is measured for the
// day and never edited. The charges shown per day come from the backend's
// preview — nothing here adds rupees.

const fmt = (n) =>
  `₹ ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const dateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const Tag = ({ children, tone }) => (
  <Box
    component="span"
    sx={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      bgcolor: tone === 'end' ? '#eaf6eb' : '#f4f4f4',
      color: tone === 'end' ? '#1e5a20' : '#969696',
    }}
  >
    {children}
  </Box>
);

const DayLog = ({ days, edits, onEdit, priced, disabled }) => {
  const pricedByDay = new Map((priced || []).map((d) => [Number(d?.day), d]));

  return (
    <Box
      sx={{
        maxHeight: 520,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        border: '1px solid #e8e8e8',
        borderRadius: 1.5,
        bgcolor: '#f9fafc',
        px: 1.5,
        pb: 1.5,
      }}
    >
      {(days || []).map((rec) => {
        const n = Number(rec?.day);
        const e = edits?.[n] || {};
        const last = rec?.closedBy === 'end';
        const s = new Date(e.startTime).getTime();
        const en = new Date(e.endTime).getTime();
        const hours =
          Number.isFinite(s) && Number.isFinite(en) && en >= s
            ? (en - s) / 36e5
            : num(rec?.totalHours);
        const gps = rec?.gpsDistance ?? rec?.totalDistance ?? 0;
        const os = num(e.odometerStart);
        const oe = num(e.odometerEnd);
        const odoOk = os > 0 && oe > 0 && oe >= os;
        const odoDist = odoOk ? oe - os : null;
        const charges = pricedByDay.get(n)?.charges || rec?.charges || null;

        return (
          <Box
            key={n}
            sx={{
              border: '1px solid #e8e8e8',
              borderRadius: 1.5,
              bgcolor: '#fff',
              p: 1.5,
              mt: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 1.5,
                flexWrap: 'wrap',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: '#fff',
                py: 0.5,
              }}
            >
              <Typography variant="body2" className="fw600">
                Day {n}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dateLabel(e.startTime || rec?.startTime || rec?.date)}
              </Typography>
              <Tag tone={last ? 'end' : 'pause'}>{last ? 'closed by End' : 'closed by pause'}</Tag>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={e.startTime ?? ''}
                  disabled={disabled}
                  onChange={(ev) => onEdit(n, 'startTime', ev.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={n === 1 ? 'driver pressed Start' : 'driver resumed'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={e.endTime ?? ''}
                  disabled={disabled}
                  onChange={(ev) => onEdit(n, 'endTime', ev.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={last ? 'driver pressed End' : 'driver paused'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Hours"
                  fullWidth
                  size="small"
                  value={hours.toFixed(2)}
                  disabled
                  InputProps={{ endAdornment: <InputAdornment position="end">hr</InputAdornment> }}
                  helperText="end − start"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="GPS Distance"
                  fullWidth
                  size="small"
                  value={Number(gps).toFixed(2)}
                  disabled
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                  helperText="trail for this day, not editable"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Odometer"
                  type="number"
                  fullWidth
                  size="small"
                  value={e.odometerStart ?? ''}
                  disabled={disabled}
                  onChange={(ev) => onEdit(n, 'odometerStart', ev.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                  helperText="enter to bill on the odometer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Odometer"
                  type="number"
                  fullWidth
                  size="small"
                  value={e.odometerEnd ?? ''}
                  disabled={disabled}
                  onChange={(ev) => onEdit(n, 'odometerEnd', ev.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Odometer Distance"
                  fullWidth
                  size="small"
                  value={odoDist === null ? '—' : odoDist}
                  disabled
                  InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                  helperText="end − start odometer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    minHeight: 40,
                    border: '1px dashed #c9c9c9',
                    borderRadius: 1,
                  }}
                >
                  <FormLabel sx={{ fontSize: 12 }}>Bill on</FormLabel>
                  <RadioGroup
                    row
                    value={odoOk ? e.distanceSource || 'gps' : 'gps'}
                    onChange={(ev) => onEdit(n, 'distanceSource', ev.target.value)}
                  >
                    <FormControlLabel
                      value="gps"
                      control={<Radio size="small" />}
                      label="GPS"
                      disabled={disabled}
                    />
                    <FormControlLabel
                      value="odometer"
                      control={<Radio size="small" />}
                      label="Odometer"
                      disabled={disabled || !odoOk}
                    />
                  </RadioGroup>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                gap: '4px 16px',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                mt: 1.25,
                fontSize: 12.5,
                color: '#969696',
              }}
            >
              {charges ? (
                <>
                  <span>package {fmt(charges.subtotal)}</span>
                  <span style={{ color: charges.extraHrs ? '#8a6a1f' : undefined }}>
                    {charges.extraHrs ? `extra hours ${fmt(charges.extraHrs)}` : 'within the hours'}
                  </span>
                  <span style={{ color: charges.extraKM ? '#8a6a1f' : undefined }}>
                    {charges.extraKM ? `extra km ${fmt(charges.extraKM)}` : 'within the km'}
                  </span>
                  <Typography
                    component="span"
                    className="fw600"
                    sx={{ ml: 'auto', color: '#1e5a20', fontSize: 13.5 }}
                  >
                    Day total{' '}
                    {fmt(
                      (Number(charges.subtotal) || 0) +
                        (Number(charges.extraHrs) || 0) +
                        (Number(charges.extraKM) || 0),
                    )}
                  </Typography>
                </>
              ) : (
                <span>priced when the fare is recalculated</span>
              )}
            </Box>
          </Box>
        );
      })}
      {!days?.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          No day records on this trip.
        </Typography>
      )}
    </Box>
  );
};

export default DayLog;
